import { Link } from "react-router-dom";
import { Flag, Globe2, ArrowUpRight } from "lucide-react";
import { SERVICE_TYPES } from "@/lib/data";

const TYPE_ICONS = { us: Flag, eu: Globe2 };

export default function ServiceTracks({ heading, kicker, blurb, compact = false }) {
    return (
        <section className={`container-page ${compact ? "py-12 md:py-16" : "section"}`} data-testid="home-service-tracks">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 mb-10 items-end">
                <div className="lg:col-span-7">
                    {kicker && <p className="overline mb-3">{kicker}</p>}
                    <h2 className="display text-4xl md:text-5xl lg:text-6xl text-brand-ink leading-[1.05]">
                        {heading}
                    </h2>
                </div>
                {blurb && (
                    <div className="lg:col-span-5">
                        <p className="text-brand-ink/75 leading-relaxed">{blurb}</p>
                    </div>
                )}
            </div>

            <div className="grid sm:grid-cols-2 gap-4 md:gap-6">
                {SERVICE_TYPES.map((t, i) => {
                    const Icon = TYPE_ICONS[t.id];
                    return (
                        <Link
                            key={t.id}
                            to={`/services?track=${t.id}`}
                            data-testid={`home-track-${t.id}`}
                            className="group relative text-left rounded-2xl p-7 md:p-8 border bg-white text-brand-ink border-brand-border overflow-hidden animate-fade-up"
                            style={{
                                animationDelay: `${i * 90}ms`,
                                boxShadow: "0 1px 0 rgba(20,34,57,0.04), 0 14px 30px -22px rgba(20,34,57,0.18)",
                                transition: "transform 0.35s ease, box-shadow 0.35s ease, background-color 0.35s ease",
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "translateY(-4px)";
                                e.currentTarget.style.boxShadow =
                                    "0 1px 0 rgba(20,34,57,0.06), 0 28px 50px -22px rgba(20,34,57,0.28)";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "translateY(0)";
                                e.currentTarget.style.boxShadow =
                                    "0 1px 0 rgba(20,34,57,0.04), 0 14px 30px -22px rgba(20,34,57,0.18)";
                            }}
                        >
                            <div className="flex items-start justify-between mb-5">
                                <span className="grid place-items-center w-12 h-12 rounded-xl bg-brand-creamLight text-brand-ink">
                                    <Icon size={22} strokeWidth={1.6} />
                                </span>
                                <span className="text-[10px] uppercase tracking-[0.22em] text-brand-muted">
                                    {t.kicker} · {t.services.length} services
                                </span>
                            </div>
                            <h3 className="font-serif text-2xl md:text-3xl leading-tight text-brand-ink">
                                {t.title}
                            </h3>
                            <p className="overline mt-2 text-brand-amber">{t.audience}</p>
                            <p className="mt-4 text-sm leading-relaxed text-brand-ink/70">
                                {t.summary}
                            </p>

                            <ul className="mt-5 space-y-1.5">
                                {t.services.slice(0, 3).map(s => (
                                    <li key={s.slug} className="flex items-center gap-2 text-sm text-brand-ink/80">
                                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand-amber shrink-0" />
                                        {s.title}
                                    </li>
                                ))}
                                {t.services.length > 3 && (
                                    <li className="text-xs text-brand-muted pl-3.5">
                                        +{t.services.length - 3} more
                                    </li>
                                )}
                            </ul>

                            <span className="mt-7 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-ink">
                                Explore this track
                                <ArrowUpRight
                                    size={16}
                                    className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                                />
                            </span>
                        </Link>
                    );
                })}
            </div>
        </section>
    );
}
